import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, RefreshCw, AlertCircle, CheckCircle, AlertTriangle, Ban, Clock } from 'lucide-react';
import { InputField } from '../common';
import { getAuthHeaders } from '../../utils/authUtils';
import { getCountries, getRegions } from '../../services/api';
import QuotaModal from './QuotaModal';
import Select from 'react-select';

const SearchFormWithQuota = ({ 
  searchParams, 
  setSearchParams, 
  setIsDateInvalid, 
  user,
  stripeCustomerId,
  quotaRefreshTrigger,
  isSearching = false,
  onQuotaStatusChange,
  onQuotaDataChange
}) => {
  const navigate = useNavigate();
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaData, setQuotaData] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const [generalError, setGeneralError] = useState(null);

  const updateParam = (key, value) => {
    setSearchParams(prev => ({ ...prev, [key]: value }));
  };

  const dateFormats = ['Daily', 'Monthly'];

  const [destinationInputs, setDestinationInputs] = useState({
    airport: '',
    country: [],
    region: [],
  });

  const [destinationType, setDestinationType] = useState(searchParams.destinationType || 'airport');

  const currentInput = destinationInputs[destinationType] || [];

  const [allCountries, setAllCountries] = useState([]);
  const [allRegions, setAllRegions] = useState([]);

  // Helper functions for trial
  const isUserOnTrial = () => {
    return user?.subscriptionStatus === 'trialing' && user?.trialEnd;
  };

  const getTrialDaysRemaining = () => {
    if (!isUserOnTrial()) return null;
    
    const trialEndDate = new Date(user.trialEnd);
    const today = new Date();
    const diffTime = trialEndDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const formatTrialEndDate = () => {
    if (!user?.trialEnd) return '';
    
    const trialEndDate = new Date(user.trialEnd);
    return trialEndDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Fetch quota data
  const fetchQuotaData = async () => {
    if (!user.id) return;
    
    setQuotaLoading(true);
    setQuotaError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/quota/user/${user.id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch quota data');
      
      const data = await response.json();
      setQuotaData(data);
      
      if (onQuotaDataChange) onQuotaDataChange(data);
    } catch (err) {
      console.error('Quota fetch error:', err);
      setGeneralError('Sorry, there was an issue running the report please reach out to travel@skedguy.com');
      setQuotaData(null);
      if (onQuotaDataChange) onQuotaDataChange(null);
    } finally {
      setQuotaLoading(false);
    }
  };

  const isQuotaExceeded = () => {
    if (!quotaData) return false;
    return (quotaData.remainingQuotaSum || 0) <= 0;
  };

  const getQuotaStatus = () => {
    if (!quotaData || !quotaData.quotas || quotaData.quotas.length === 0) {
      return { color: 'primary', icon: Plane, text: 'Unknown' };
    }
    
    const expiringQuota = quotaData.quotas.find(q => !q.neverExpire);
    const permanentQuota = quotaData.quotas.find(q => q.neverExpire);
    
    if (expiringQuota && expiringQuota.remainingQuota > 0) {
      const usagePercentage = (expiringQuota.consumedRows / expiringQuota.totalQuota) * 100;
      
      if (usagePercentage >= 90) {
        return { color: 'warning', icon: AlertTriangle, text: 'Monthly Ending' };
      } else if (usagePercentage >= 75) {
        return { color: 'info', icon: CheckCircle, text: 'Using Monthly' };
      } else {
        return { color: 'success', icon: CheckCircle, text: 'Healthy' };
      }
    }
    
    if (permanentQuota) {
      const usagePercentage = (permanentQuota.consumedRows / permanentQuota.totalQuota) * 100;
      
      if (permanentQuota.remainingQuota <= 0) {
        return { color: 'danger', icon: Ban, text: 'All Exhausted' };
      } else if (usagePercentage >= 90) {
        return { color: 'danger', icon: AlertCircle, text: 'Paid Critical' };
      } else if (usagePercentage >= 75) {
        return { color: 'warning', icon: AlertTriangle, text: 'Using Paid' };
      } else {
        return { color: 'success', icon: CheckCircle, text: 'Using Paid' };
      }
    }
    
    return { color: 'danger', icon: Ban, text: 'No Quota' };
  };

  const getOverallUsagePercentage = () => {
    if (!quotaData) return 0;
    const totalQuota = quotaData.totalQuotaSum || 1;
    const consumedRows = quotaData.consumedRowsSum || 0;
    return Math.round((consumedRows / totalQuota) * 100);
  };

  const quotaStatus = getQuotaStatus();
  const trialDaysRemaining = getTrialDaysRemaining();

  useEffect(() => {
    if (onQuotaStatusChange) {
      onQuotaStatusChange(isQuotaExceeded());
    }
  }, [quotaData, onQuotaStatusChange]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSearchParams(prev => {
      const updated = { ...prev };
      if (!prev.startDate) updated.startDate = today;
      if (!prev.endDate) updated.endDate = today;
      return updated;
    });
  }, []);

  useEffect(() => {
    fetchQuotaData();
  }, [user.id, quotaRefreshTrigger]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [countries, regions] = await Promise.all([getCountries(), getRegions()]);
        if (isMounted) {
          setAllCountries(countries.map(c => ({ label: c.name, value: c.code || c.name })));
          setAllRegions(regions.map(r => ({ label: r.name, value: r.code || r.name })));
        }
      } catch (err) {
        console.error('Failed to fetch destination data:', err);
        setGeneralError('Sorry, there was an issue running the report please reach out to travel@skedguy.com');
      }
    };

    fetchData();

    return () => { isMounted = false; };
  }, []);

  const destinationOptions = destinationType === 'country' ? allCountries : destinationType === 'region' ? allRegions : [];

  const isInvalidDateRange =
    searchParams.startDate && searchParams.endDate &&
    new Date(searchParams.endDate) < new Date(searchParams.startDate);

  useEffect(() => {
    setIsDateInvalid(isInvalidDateRange);
  }, [isInvalidDateRange, setIsDateInvalid]);

  // Handlers
  const handleAirportChange = (val) => {
    setDestinationInputs(prev => ({ ...prev, airport: val }));
    updateParam('destination', val);
  };

  const handleMultiChange = (selectedOptions) => {
    const values = selectedOptions || [];
    setDestinationInputs(prev => ({
      ...prev,
      [destinationType]: values,
    }));
    updateParam('destination', values.map(opt => opt.value));
  };

  const handleRadioChange = (e) => {
    const newType = e.target.value;
    setDestinationType(newType);
    updateParam('destinationType', newType);

    const storedInput = destinationInputs[newType];
    if (newType === 'airport') {
      updateParam('destination', storedInput || '');
    } else {
      updateParam('destination', storedInput?.map(opt => opt.value) || []);
    }
  };

  const handleStartDateChange = (newStartDate) => {
    const start = new Date(newStartDate);
    const min = new Date(minStartDate);
    const max = new Date(maxEndDate);

    if (start < min) {
      alert(`Start date cannot be before ${minStartDate}`);
      updateParam('startDate', minStartDate);
      return;
    }
    if (start > max) {
      alert(`Start date cannot exceed ${maxEndDate}`);
      updateParam('startDate', maxEndDate);
      return;
    }

    updateParam('startDate', newStartDate);

    if (!searchParams.endDate || new Date(searchParams.endDate) < start) {
      updateParam('endDate', newStartDate);
    }
  };

  const handleEndDateChange = (newEndDate) => {
    const end = new Date(newEndDate);
    const min = new Date(minStartDate);
    const max = new Date(maxEndDate);
    const start = new Date(searchParams.startDate);

    if (end < min) {
      alert(`End date cannot be before ${minStartDate}`);
      updateParam('endDate', minStartDate);
      return;
    }
    if (end > max) {
      alert(`End date cannot exceed ${maxEndDate}`);
      updateParam('endDate', maxEndDate);
      return;
    }
    if (end < start) {
      alert('End date cannot be before Start date');
      updateParam('endDate', searchParams.startDate);
      return;
    }

    updateParam('endDate', newEndDate);
  };

  const getTodayString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getMaxEndDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 315);
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const day = String(maxDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const minStartDate = getTodayString();
  const maxEndDate = getMaxEndDate();

  const handleQuotaButtonClick = () => {
    setShowQuotaModal(true);
    fetchQuotaData();
  };

  return (
    <>
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          {generalError && (
            <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
              <AlertTriangle size={16} className="me-2" />
              {generalError}
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setGeneralError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
            <h5 className="card-title d-flex align-items-center mb-0">
              <Plane className="me-2 text-primary" size={20} />
              Schedule Search
            </h5>
            
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Trial Status Display */}
              {isUserOnTrial() && (
                <div className="d-flex align-items-center text-info small">
                  <Clock size={14} className="me-1" />
                  <span className="text-muted ms-1">
                    (Trial ends {formatTrialEndDate()})
                  </span>
                </div>
              )}

              {/* Quota Status Button */}
              <button 
                className={`btn btn-outline-${quotaStatus.color} btn-sm d-flex align-items-center ${quotaLoading ? 'disabled' : ''}`}
                onClick={handleQuotaButtonClick}
                title={`View Row Quota Status - ${quotaStatus.text}`}
                disabled={quotaLoading}
              >
                {quotaLoading ? (
                  <RefreshCw size={14} className="me-1 spinner-border spinner-border-sm" />
                ) : (
                  <quotaStatus.icon size={14} className="me-1" />
                )}
                Quota Status
                {quotaData && (
                  <span className={`badge bg-${quotaStatus.color} ms-2`}>
                    {getOverallUsagePercentage()}%
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="row">
            {/* Origin */}
            <div className="col-12 col-md-3 mb-3 border-start border-2 border-light ps-4">
              <label className="form-label fw-medium">Origin:</label>
              <InputField
                value={searchParams.origin}
                onChange={val => updateParam('origin', val)}
                placeholder="e.g., DFW"
                disabled={isQuotaExceeded()}
              />
            </div>

            {/* Destination */}
            <div className="col-12 col-md-3 mb-3 border-start border-2 border-light ps-4">
              <label className="form-label fw-medium">Destination:</label>
              {destinationType === 'airport' ? (
                <InputField
                  value={destinationInputs.airport}
                  onChange={handleAirportChange}
                  placeholder="e.g., ORD"
                  disabled={isQuotaExceeded()}
                />
              ) : (
                <div className="mt-2">
                  <Select
                    isMulti
                    options={destinationOptions}
                    value={currentInput}
                    onChange={handleMultiChange}
                    placeholder={`Select ${destinationType}`}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isDisabled={isQuotaExceeded()}
                  />
                </div>
              )}

              <div className="mt-3 d-flex flex-wrap">
                {['airport', 'country', 'region'].map(type => (
                  <div key={type} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="destinationType"
                      id={`dest-${type}`}
                      value={type}
                      checked={destinationType === type}
                      onChange={handleRadioChange}
                      disabled={isQuotaExceeded()}
                    />
                    <label className="form-check-label" htmlFor={`dest-${type}`}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Carrier */}
            <div className="col-12 col-md-3 mb-3 border-start border-2 border-light ps-4">
              <label className="form-label fw-medium">Carrier:</label>
              <InputField
                value={searchParams.opcarrier}
                onChange={val => updateParam('opcarrier', val)}
                placeholder="e.g., UA"
                disabled={isQuotaExceeded()}
              />
              <label className="form-label fw-medium mt-1">Aircraft Type:</label>
              <InputField
                value={searchParams.aircrafttype}
                onChange={val => updateParam('aircrafttype', val)}
                placeholder="e.g., 738"
                disabled={isQuotaExceeded()}
              />
            </div>

            {/* Date & Format */}
            <div className="col-12 col-md-3 mb-3 border-start border-2 border-light ps-4">
              <div className="mb-3">
                <label className="form-label fw-medium">Start Date:</label>
                <input
                  type="date"
                  className="form-control"
                  value={searchParams.startDate}
                  onChange={e => handleStartDateChange(e.target.value)}
                  min={minStartDate}
                  max={maxEndDate}
                  disabled={isQuotaExceeded()}
                  style={{ colorScheme: 'light' }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">End Date:</label>
                <input
                  type="date"
                  className="form-control"
                  value={searchParams.endDate}
                  onChange={e => handleEndDateChange(e.target.value)}
                  min={minStartDate}
                  max={maxEndDate}
                  inputMode="numeric"
                  disabled={isQuotaExceeded()}
                  style={{ colorScheme: 'light' }}
                />
                {isInvalidDateRange && (
                  <div className="text-danger small mt-1">
                    <AlertCircle size={12} className="me-1" />
                    End Date cannot be before Start Date
                  </div>
                )}
              </div>

              <div>
                <label className="form-label fw-medium">Display Format:</label>
                <select
                  className="form-select"
                  value={searchParams.displayDateFormat}
                  onChange={e => updateParam('displayDateFormat', e.target.value)}
                  disabled={isQuotaExceeded()}
                >
                  {dateFormats.map(format => (
                    <option key={format} value={format}>{format}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuotaModal 
        show={showQuotaModal}
        onHide={() => setShowQuotaModal(false)}
        userId={user.id}
      />
    </>
  );
};

export default SearchFormWithQuota;